import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import { formatDateTime } from "@/utils/dates";
import { getExecutionBadge } from ".";
import { Icon } from "@elaniintc/goes-design-system";

interface Props {
  execution: ApiFlowsExecutionsResponseType["data"][number]["node_executions"][number];
}

// Mapeo de canales de notificación
const channelLabels: Record<string, string> = {
  email: "Correo electrónico",
  sms: "SMS",
  push: "Notificación push",
  whatsapp: "WhatsApp",
};

const NotificationSimpleContent = ({ execution }: Props) => {
  if (!execution || !execution.inputs || !execution.outputs) {
    return (
      <div className="p-4 border border-error-quiet rounded-lg bg-error-silent flex gap-3 items-start">
        <Icon
          type="fas"
          name="exclamation-triangle"
          className="text-error py-2.5"
          size="sm"
          aria-label="Error"
        />
        <div>
          <p className="font-semibold text-error-loud">
            No se pudo cargar la información
          </p>
          <p className="text-sm text-error mt-1">
            No hay datos de ejecución disponibles para este nodo.
          </p>
        </div>
      </div>
    );
  }

  const channelInput = execution?.inputs?.find(
    (input) => input.name === "channel",
  );
  const titleInput = execution?.inputs?.find((input) => input.name === "title");
  const descriptionInput = execution?.inputs?.find(
    (input) => input.name === "description",
  );
  const recipientInputs = execution?.inputs?.filter(
    (input) =>
      input.name === "recipient" &&
      input.value !== null &&
      String(input.value).trim() !== "",
  );
  const dispatchedOutput = execution?.outputs?.find(
    (output) => output.name === "dispatched",
  );

  const channel = String(channelInput?.value || "");
  const channelLabel = channelLabels[channel] || channel;
  const title = String(titleInput?.value || "");
  const description = String(descriptionInput?.value || "");
  const wasDispatched = dispatchedOutput?.value === true;

  // Extraer texto plano del HTML (descripción)
  const stripHtml = (html: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <div className="border border-neutral rounded-lg p-4 bg-neutral-object flex flex-col gap-1">
      <p>Estado del nodo: {getExecutionBadge(execution?.status)}</p>
      <p>Se completó el: {formatDateTime(execution?.ended_at || "")}</p>

      <div className="mt-2">
        <p className="font-semibold">Canal de notificación:</p>
        <p className="pl-4">{channelLabel}</p>
      </div>

      <div className="mt-2">
        <p className="font-semibold">Título:</p>
        <p className="pl-4">{title}</p>
      </div>

      <div className="mt-2">
        <p className="font-semibold">Descripción:</p>
        <p className="pl-4">{stripHtml(description)}</p>
      </div>

      <div className="mt-2">
        <p className="font-semibold">
          Destinatarios ({recipientInputs?.length}):
        </p>
        <div className="pl-4">
          {recipientInputs?.length === 0 ? (
            <p className="text-neutral-quiet">
              No hay destinatarios registrados
            </p>
          ) : (
            recipientInputs?.map((recipient, index) => (
              <p key={recipient.id || index}>
                {index + 1}. {String(recipient.value)}
              </p>
            ))
          )}
        </div>
      </div>

      <div className="mt-2">
        <p className="font-semibold">Estado de envío:</p>
        <p className="pl-4">
          {wasDispatched ? (
            <span className="text-success-loud font-semibold">
              ✓ Enviado correctamente
            </span>
          ) : (
            <span className="text-danger-loud font-semibold">
              ✗ No se pudo enviar
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default NotificationSimpleContent;
